import type { ReactNode } from "react";

import {
  SITE_URL,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "@/pages/learn/learnShared";
import { esBaseCrumbs } from "./learnFactoryEs";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const link = (text: string, to: string) => (
  <a
    href={to}
    className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
  >
    {text}
  </a>
);

const AnswerBlock = ({ children }: { children: ReactNode }) => (
  <div className="not-prose mb-5 rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
    <p className="text-[1.0625rem] leading-relaxed text-foreground">
      {children}
    </p>
  </div>
);

const a_beam =
  "El m\u00e9todo BEAM F3 es una forma r\u00e1pida de encontrar un punto est\u00e1ndar en el cuero cabelludo \u2014 la posici\u00f3n F3 \u2014 a partir de tres medidas de la cabeza, usado como sustituto de la corteza prefrontal dorsolateral izquierda. Se desarroll\u00f3 para facilitar la TMS est\u00e1ndar sin neuronavegaci\u00f3n.";

const a_fmri =
  "Una resonancia magn\u00e9tica cerebral en reposo muestra qu\u00e9 parte de su corteza prefrontal trabaja en sincron\u00eda u oposici\u00f3n con un \u00e1rea m\u00e1s profunda relacionada con el \u00e1nimo (la circunvoluci\u00f3n subgenual). El software de SAINT\u00ae usa esos datos de conectividad para elegir el punto exacto a estimular en usted \u2014 un objetivo individual, no un promedio.";

const a_same =
  "No. Las medidas de la cabeza estiman d\u00f3nde suele estar la regi\u00f3n en una cabeza promedio; no pueden ver la funci\u00f3n cerebral. El objetivo de conectividad es distinto en cada persona, as\u00ed que una estimaci\u00f3n del cuero cabelludo y un objetivo basado en resonancia no coinciden de forma fiable. Por eso SAINT\u00ae requiere la resonancia.";

const a_saint =
  "No. SAINT\u00ae requiere un objetivo personalizado por resonancia, software de orientaci\u00f3n autorizado por la FDA y el sistema Magnus Medical \u2014 solo de proveedores en el localizador de Magnus Medical. Una cl\u00ednica que diga que las medidas de la cabeza usan \u201clas mismas medidas y algoritmo\u201d que una resonancia no est\u00e1 describiendo SAINT\u00ae.";

const a_better =
  "El ensayo controlado de SAINT\u00ae (79 % de remisi\u00f3n) us\u00f3 orientaci\u00f3n por resonancia; esos resultados no se trasladan a programas que orientan de otra forma. Pregunte a su m\u00e9dico qu\u00e9 m\u00e9todo usa una cl\u00ednica y qu\u00e9 resultados tiene. Los resultados individuales var\u00edan.";

const faqs: LearnFAQ[] = [
  { question: "\u00bfQu\u00e9 es el m\u00e9todo BEAM F3?", answer: a_beam },
  {
    question: "\u00bfQu\u00e9 es la TMS guiada por fMRI (conectividad)?",
    answer: a_fmri,
  },
  {
    question:
      "\u00bfPueden las medidas de la cabeza encontrar el mismo objetivo que una fMRI?",
    answer: a_same,
  },
  { question: "\u00bfEs BEAM F3 lo mismo que SAINT?", answer: a_saint },
  {
    question:
      "\u00bfCu\u00e1l es mejor para la depresi\u00f3n resistente al tratamiento?",
    answer: a_better,
  },
];

const canonical = "/es/learn/beam-f3-vs-fmri-tms";

export const esBeamF3VsFmri: LearnPageData = {
  slug: "beam-f3-vs-fmri-tms",
  title:
    "BEAM F3 vs orientaci\u00f3n por fMRI para TMS: \u00bfcu\u00e1l es la diferencia?",
  description:
    "BEAM F3 estima un objetivo cerebral a partir de medidas de la cabeza; la SAINT\u00ae guiada por fMRI encuentra su propio objetivo a partir de una resonancia. Esto es por qu\u00e9 importa.",
  canonical,
  h1: "BEAM F3 vs TMS guiada por fMRI: c\u00f3mo apuntan el im\u00e1n las cl\u00ednicas",
  breadcrumbLabel: "BEAM F3 vs orientaci\u00f3n por fMRI",
  crumbs: esBaseCrumbs("BEAM F3 vs orientaci\u00f3n por fMRI"),
  shortAnswer:
    "BEAM F3 estima d\u00f3nde suele estar una regi\u00f3n cerebral a partir de tres medidas de la cabeza. La SAINT\u00ae guiada por fMRI encuentra su propio objetivo a partir de una resonancia. La diferencia importa porque el objetivo de conectividad es distinto en cada persona, y solo el enfoque basado en resonancia y autorizado por la FDA es SAINT\u00ae.",
  keyTakeaways: [
    "BEAM F3 encuentra un punto est\u00e1ndar del cuero cabelludo (la posici\u00f3n F3) a partir de medidas \u2014 un sustituto promedio, no un objetivo personal.",
    "La SAINT\u00ae guiada por fMRI usa la conectividad funcional de su propio cerebro para elegir d\u00f3nde estimular.",
    "Las medidas de la cabeza no ven la funci\u00f3n cerebral, as\u00ed que no coinciden de forma fiable con un objetivo de resonancia.",
    "SAINT\u00ae requiere el objetivo por resonancia, software autorizado por la FDA y el sistema Magnus Medical \u2014 solo de proveedores verificados.",
    "El 79 % de remisi\u00f3n proviene de la SAINT\u00ae guiada por resonancia; no se traslada a otro m\u00e9todo de orientaci\u00f3n.",
  ],
  sections: [
    {
      heading: "\u00bfQu\u00e9 es el m\u00e9todo BEAM F3?",
      body: (
        <>
          <AnswerBlock>{a_beam}</AnswerBlock>
          {P(
            "La \u201cF3\u201d de BEAM F3 se refiere a la posici\u00f3n del electrodo F3 \u2014 un punto de referencia est\u00e1ndar en el cuero cabelludo que aproxima la corteza prefrontal dorsolateral izquierda, el \u00e1rea que la mayor\u00eda de los protocolos de TMS estimulan para la depresi\u00f3n. Medir la cabeza y llegar a esa coordenada promedio es r\u00e1pido y no necesita esc\u00e1ner, por eso muchas cl\u00ednicas de TMS est\u00e1ndar lo usan.",
          )}
          {P(
            "Pero \u201ccoordenada promedio\u201d es la frase clave: BEAM F3 indica d\u00f3nde suele estar esa regi\u00f3n, no d\u00f3nde est\u00e1 en usted, y nada sobre c\u00f3mo se comunica con los circuitos m\u00e1s profundos del \u00e1nimo que la TMS busca influir.",
          )}
        </>
      ),
    },
    {
      heading: "\u00bfQu\u00e9 es la TMS guiada por fMRI (conectividad)?",
      body: (
        <>
          <AnswerBlock>{a_fmri}</AnswerBlock>
          {P(
            "Antes del tratamiento con SAINT\u00ae, usted se hace una resonancia magn\u00e9tica funcional en reposo. El software analiza la conectividad funcional \u2014 c\u00f3mo hablan entre s\u00ed las regiones cerebrales \u2014 y elige el punto exacto de su corteza prefrontal con el v\u00ednculo m\u00e1s fuerte con la circunvoluci\u00f3n subgenual, un \u00e1rea m\u00e1s profunda implicada en el \u00e1nimo. Ese punto es \u00fanico en usted y se convierte en el objetivo de las 50 sesiones.",
          )}
        </>
      ),
    },
    {
      heading: "Dos formas de apuntar el im\u00e1n",
      body: (
        <>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <svg
                viewBox="0 0 200 120"
                className="mb-3 h-28 w-full"
                role="img"
                aria-label="Contorno de cabeza con l\u00edneas de medici\u00f3n que apuntan a un punto promedio"
              >
                <ellipse
                  cx="100"
                  cy="62"
                  rx="42"
                  ry="52"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-primary"
                />
                <line
                  x1="40"
                  y1="58"
                  x2="160"
                  y2="58"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  className="text-accent"
                />
                <line
                  x1="100"
                  y1="10"
                  x2="100"
                  y2="114"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  className="text-accent"
                />
                <circle
                  cx="78"
                  cy="50"
                  r="6"
                  fill="currentColor"
                  className="text-accent"
                />
              </svg>
              <p className="text-sm font-semibold text-foreground">
                Medidas de la cabeza
              </p>
              <p className="text-sm text-foreground/80">
                \u2192 un punto promedio en una cabeza promedio.
              </p>
            </div>
            <div className="rounded-xl border border-accent/40 bg-accent/10 p-4">
              <svg
                viewBox="0 0 200 120"
                className="mb-3 h-28 w-full"
                role="img"
                aria-label="Contorno de resonancia con un objetivo resaltado \u00fanico para ese cerebro"
              >
                <path
                  d="M55 95 C30 70 35 35 70 28 C80 14 120 14 130 28 C165 35 170 70 145 95 C135 108 65 108 55 95 Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-primary"
                />
                <path
                  d="M100 28 L100 60"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  className="text-foreground/40"
                />
                <path
                  d="M70 60 Q100 78 130 60"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  className="text-foreground/40"
                />
                <circle
                  cx="82"
                  cy="52"
                  r="7"
                  fill="currentColor"
                  className="text-accent"
                />
                <circle
                  cx="118"
                  cy="74"
                  r="5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-accent"
                />
              </svg>
              <p className="text-sm font-semibold text-foreground">
                Resonancia cerebral (fMRI)
              </p>
              <p className="text-sm text-foreground/80">
                \u2192 su punto, seg\u00fan su propia conectividad.
              </p>
            </div>
          </div>
          {P(
            <>
              Ambos enfoques terminan con una bobina sobre su cabeza. La
              diferencia es c\u00f3mo se elige esa coordenada.{" "}
              {link(
                "\u00bfRequiere SAINT\u00ae una resonancia?",
                "/es/learn/does-saint-tms-require-an-mri",
              )}{" "}
              explica por qu\u00e9 la resonancia es obligatoria, no opcional.
            </>,
          )}
        </>
      ),
    },
    {
      heading:
        "\u00bfPueden las medidas de la cabeza encontrar el mismo objetivo que una fMRI?",
      body: (
        <>
          <AnswerBlock>{a_same}</AnswerBlock>
          {P(
            "Una posici\u00f3n del cuero cabelludo es un punto de partida razonable, pero la parte de la corteza prefrontal que mejor se conecta con su circunvoluci\u00f3n subgenual var\u00eda de persona a persona. Medir el exterior de su cabeza no revela esa conectividad interna, as\u00ed que un objetivo por medici\u00f3n y uno por resonancia pueden caer en sitios distintos.",
          )}
        </>
      ),
    },
    {
      heading: "\u00bfEs BEAM F3 lo mismo que SAINT?",
      body: (
        <>
          <AnswerBlock>{a_saint}</AnswerBlock>
          {P(
            <>
              SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation
              Therapy) es un protocolo espec\u00edfico autorizado por la FDA: el
              sistema Magnus, su propio software de orientaci\u00f3n y entrega
              solo por proveedores en el{" "}
              {link(
                "localizador de Magnus Medical",
                "/es/learn/what-is-a-verified-saint-provider",
              )}
              . Otras cl\u00ednicas pueden usar una resonancia para apuntar la
              TMS sin ofrecer SAINT\u00ae \u2014 es un tratamiento distinto. Vea
              nuestra{" "}
              {link(
                "comparaci\u00f3n de SAINT\u00ae frente a la TMS acelerada de menor costo",
                "/es/learn/saint-vs-accelerated-tms",
              )}
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading:
        "\u00bfCu\u00e1l es mejor para la depresi\u00f3n resistente al tratamiento?",
      body: (
        <>
          <AnswerBlock>{a_better}</AnswerBlock>
          {P(
            "No afirmamos superioridad en un estudio directo. Lo que s\u00ed podemos decir de forma factual: en el ensayo controlado, aleatorizado y doble ciego de 2022, el 79 % del grupo de tratamiento activo alcanz\u00f3 la remisi\u00f3n (Cole et al., Am J Psychiatry 2022;179(2):132\u2013141), y ese ensayo us\u00f3 orientaci\u00f3n por resonancia. Esos resultados no se pueden asumir para un programa que orienta de otra forma. Pregunte a su m\u00e9dico qu\u00e9 m\u00e9todo usa una cl\u00ednica y qu\u00e9 resultados ha publicado.",
          )}
        </>
      ),
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "3 de noviembre de 2026",
    lastReviewed: "3 de noviembre de 2026",
    lastUpdated: "3 de noviembre de 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      {
        ...webPageNode({
          id: `${SITE_URL}${canonical}#webpage`,
          name: "BEAM F3 vs TMS guiada por fMRI: c\u00f3mo apuntan el im\u00e1n las cl\u00ednicas",
          url: `${SITE_URL}${canonical}`,
          description:
            "BEAM F3 estima un objetivo cerebral a partir de medidas de la cabeza; la SAINT\u00ae guiada por fMRI encuentra su propio objetivo a partir de una resonancia. Esto es por qu\u00e9 importa.",
          about: [
            {
              "@type": "MedicalTherapy",
              name: "Transcranial Magnetic Stimulation (TMS)",
            },
            {
              "@type": "MedicalTherapy",
              name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT\u00ae)",
            },
          ],
          reviewedBy: {
            name: "Daniel Pietras, MD",
            url: `${SITE_URL}/dr-daniel-pietras-md`,
          },
          lastReviewed: "2026-11-03",
        }),
        inLanguage: "es-US",
      },
      orgNode,
      faqJsonld(faqs, `${SITE_URL}${canonical}#faq`),
      breadcrumbJsonld(esBaseCrumbs("BEAM F3 vs orientaci\u00f3n por fMRI")),
    ],
  },
  relatedLinks: [
    {
      title: "\u00bfRequiere SAINT\u00ae una resonancia?",
      href: "/es/learn/does-saint-tms-require-an-mri",
      description:
        "Por qu\u00e9 la resonancia es parte del protocolo SAINT\u00ae",
    },
    {
      title: "TMS guiada por fMRI para la depresi\u00f3n",
      href: "/es/learn/fmri-guided-tms-for-depression",
      description:
        "C\u00f3mo funciona la orientaci\u00f3n personalizada en SAINT\u00ae",
    },
    {
      title: "\u00bfQu\u00e9 es un proveedor verificado de SAINT\u00ae?",
      href: "/es/learn/what-is-a-verified-saint-provider",
      description:
        "C\u00f3mo confirmar una cl\u00ednica en el localizador de Magnus",
    },
    {
      title: "SAINT\u00ae frente a la TMS acelerada de menor costo",
      href: "/es/learn/saint-vs-accelerated-tms",
      description:
        "Qu\u00e9 es y qu\u00e9 no es un calendario de 5 d\u00edas copiado",
    },
  ],
  safetyBlock:
    "SAINT\u00ae no es para todos. Est\u00e1 contraindicado para personas con metal sensible a campos magn\u00e9ticos no extra\u00edble cerca de la cabeza, ciertos dispositivos implantados o presi\u00f3n intracraneal aumentada. Los posibles efectos secundarios incluyen cefalea, fatiga, molestias en el cuero cabelludo y ansiedad; la TMS conlleva un peque\u00f1o riesgo de convulsi\u00f3n. Si est\u00e1 en crisis, llame o env\u00ede un mensaje de texto al 988.",
  trademarkFooter:
    "SAINT\u00ae es una marca registrada de Stanford University, con licencia exclusiva a Magnus Medical, Inc. Neuro Wellness TMS Centers of America no nombra ni respalda a ninguna otra cl\u00ednica. Los resultados individuales var\u00edan. No se garantiza ning\u00fan resultado. La cobertura var\u00eda seg\u00fan el plan.",
  ctaBand: {
    headline: "Vea si SAINT\u00ae es para usted, sin costo",
    primaryText: "Vea si puedo ser candidato",
    primaryHref: "/es/learn/who-qualifies-for-saint-tms",
    secondaryText: "Hable con una persona",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
};
